import { Group, Select, Button, Paper, Text, Badge, CloseButton } from '@mantine/core';
import { IconFilter, IconX } from '@tabler/icons-react';
import type { Site, Person, Category } from '@/types/models';

export interface DashboardFilterValues {
  siteId: string | null;
  personId: string | null;
  categoryId: string | null;
}

interface DashboardFiltersProps {
  sites: Site[];
  people: Person[];
  categories: Category[];
  filters: DashboardFilterValues;
  onFiltersChange: (filters: DashboardFilterValues) => void;
}

export function DashboardFilters({
  sites,
  people,
  categories,
  filters,
  onFiltersChange,
}: DashboardFiltersProps) {
  const hasActiveFilters = filters.siteId || filters.personId || filters.categoryId;

  const clearFilters = () => {
    onFiltersChange({ siteId: null, personId: null, categoryId: null });
  };

  // Get display names for active filters
  const getActiveFilterLabels = () => {
    const labels: { key: string; label: string; value: string }[] = [];

    if (filters.siteId) {
      const site = sites.find(s => s.id === filters.siteId);
      if (site) labels.push({ key: 'siteId', label: 'Location', value: site.name });
    }

    if (filters.personId) {
      const person = people.find(p => p.id === filters.personId);
      if (person) labels.push({ key: 'personId', label: 'Person', value: person.full_name });
    }

    if (filters.categoryId) {
      const category = categories.find(c => c.id === filters.categoryId);
      if (category) labels.push({ key: 'categoryId', label: 'Category', value: category.name });
    }

    return labels;
  };

  const removeFilter = (key: keyof DashboardFilterValues) => {
    onFiltersChange({ ...filters, [key]: null });
  };

  return (
    <Paper p="md" withBorder>
      <Group gap="md" align="flex-end" wrap="wrap">
        <Group gap="xs" align="center">
          <IconFilter size={16} color="var(--mantine-color-dimmed)" />
          <Text size="sm" fw={500} c="dimmed">
            Filter Dashboard
          </Text>
        </Group>

        <Select
          placeholder="All locations"
          size="sm"
          clearable
          searchable
          data={sites.map(site => ({
            value: site.id,
            label: site.name,
          }))}
          value={filters.siteId}
          onChange={(value) => onFiltersChange({ ...filters, siteId: value })}
          style={{ minWidth: 180 }}
        />

        <Select
          placeholder="All people"
          size="sm"
          clearable
          searchable
          data={people.map(person => ({
            value: person.id,
            label: person.full_name,
          }))}
          value={filters.personId}
          onChange={(value) => onFiltersChange({ ...filters, personId: value })}
          style={{ minWidth: 180 }}
        />

        <Select
          placeholder="All categories"
          size="sm"
          clearable
          searchable
          data={categories.map(category => ({
            value: category.id,
            label: category.name,
          }))}
          value={filters.categoryId}
          onChange={(value) => onFiltersChange({ ...filters, categoryId: value })}
          style={{ minWidth: 180 }}
        />

        {hasActiveFilters && (
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            leftSection={<IconX size={14} />}
            onClick={clearFilters}
          >
            Clear all
          </Button>
        )}
      </Group>

      {/* Active filter badges */}
      {hasActiveFilters && (
        <Group gap="xs" mt="sm">
          <Text size="xs" c="dimmed">Active filters:</Text>
          {getActiveFilterLabels().map(({ key, label, value }) => (
            <Badge
              key={key}
              size="sm"
              variant="light"
              rightSection={
                <CloseButton
                  size="xs"
                  variant="transparent"
                  onClick={() => removeFilter(key as keyof DashboardFilterValues)}
                />
              }
            >
              {label}: {value}
            </Badge>
          ))}
        </Group>
      )}
    </Paper>
  );
}
